(function () {
  /**
   * Let's you resize an element's height by clicking and dragging but giving you
   * callbacks to hook into. It's up to you on what to resize and how.
   *
   * It provides three events:
   * - onStart: fired ONCE when the user starts resizing
   * - onResize: fired REPEATEDLY as the user resizes the table
   * - onEnd: fired ONCE when the user stops resizing
   *
   * The event object it passes back contains the mouse position
   * on the screen and the total height that it's moved. Looks like this:
   *
   * {
   *   screenX: 10,
   *   screenY: 25,
   *   height: -100
   * }
   *
   * @example
   * new BatchGeoVerticalResizer({
   * 	container: '.some-element',
   * 	// will return 0 in console.log since they haven't moved anywhere. Use this
   * 	// to get a starting position for screenX or screenY
   * 	onStart: function (event) { console.log(event.height) },
   *
   * 	// If the user moved it 100px up this will return 100
   * 	onEnd: function (event) { console.log(event.height) },
   *
   * 	// Updates as the user moves. In this example fires ~100 times (to get to 100px)
   * 	onResize: function (event) { console.log(event.height) }
   * })
   *
   * @param {object} options An options hash
   * @param {jQuery|HTMLElement|string} options.container The container to
   * inject the resizer element to drag and drop.
   * @param {function} [options.onStart] A callback that gives you a hook to
   * know when the resizing starts. Fired once per resize cycle.
   * @param {function} [options.onEnd] A callback that gives you a hook to
   * know when the resizing ends. Fired once per resize cycle.
   * @param {function} [options.onResize] A callback that gives you a hook to
   * know when the resizing is happening. Fired repeatedly as the user
   * resizes. You may want to debounce this.
   */
  var BatchGeoVerticalResizer = function (options) {
    this.settings = $.extend(
      true,
      {
        container: null,
        onStart: _.noop,
        onEnd: _.noop,
        onResize: _.noop,
      },
      options,
    );

    this._dragState = {
      mouseDown: false,
      startingYPosition: 0,
    };

    this._createResizerElement();
    this.render();
    this._attachBodyListeners();
  };

  /**
   * Injects the resizer element into the container. This is called
   * automatically when you initialize the class but you may need to call it
   * later for dynamic content.
   */
  BatchGeoVerticalResizer.prototype.render = function () {
    $(this.settings.container).append(this._$verticalResizerElement);
  };

  /**
   * Returns the event name for the device based on if it supports touch or
   * not and will give them standard names of up (mouseup/touchend),
   * down (mousedown/touchstart) and move (mousemove/touchmove)
   *
   * @return {object} Containing {down: '...', move: '...', up: '...'}
   *
   * @private
   */
  BatchGeoVerticalResizer.prototype._getEventNames = function () {
    if ("ontouchstart" in window) {
      return {
        down: "touchstart.BatchGeoVerticalResizer",
        move: "touchmove.BatchGeoVerticalResizer",
        up: "touchend.BatchGeoVerticalResizer",
      };
    } else {
      return {
        down: "mousedown.BatchGeoVerticalResizer",
        move: "mousemove.BatchGeoVerticalResizer",
        up: "mouseup.BatchGeoVerticalResizer",
      };
    }
  };

  /**
   * Gets the proper event object from a jQuery event. If it's a mouse event
   * then the jQuery event is fine, but if it's a touch event we need to get
   * the first touch event that happened.
   *
   * @param  {object} event a jQuery event object
   * @return {object} Either the passed in jQuery object or a touch event
   *
   * @private
   */
  BatchGeoVerticalResizer.prototype._findEvent = function (event) {
    // If its a touch(start|move|end) event use the special touches object
    return event.type.indexOf("touch") === 0
      ? event.originalEvent.touches[0]
      : event;
  };

  /**
   * Creates a resizer element and attaches it to the prototype under
   * this._$verticalResizerElement. It also attaches event listeners needed on
   * that element.
   *
   * @private
   */
  BatchGeoVerticalResizer.prototype._createResizerElement = function () {
    var self = this;
    this._$verticalResizerElement = $("<div />", {
      addClass: "batchgeo-vertical-resizer",
    }).append(
      $("<div />", {
        addClass: "batchgeo-vertical-arrow-container",
      }).append(
        $("<span />", {
          addClass: "batchgeo-vertical-arrow",
        }),
      ),
    );

    this._$verticalResizerElement.on(
      this._getEventNames().down,
      function (event) {
        self._dragState.mouseDown = true;
        self._dragState.startingYPosition = self._findEvent(event).screenY;
        self.settings.onStart(self._createEventObject(event, 0));
      },
    );
  };

  /**
   * Creates a public event object to use with the event API
   * @param  {object} localEvent  A jQuery event object
   * @param  {number} height The total height that the resizer has moved
   * @return {object} An object containing screenX, screenY, and height
   */
  BatchGeoVerticalResizer.prototype._createEventObject = function (
    event,
    height,
  ) {
    let localEvent = this._findEvent(event);

    if (!localEvent) return;

    return {
      screenX: localEvent.screenX,
      screenY: localEvent.screenY,
      height: height,
    };
  };

  /**
   * Attaches the listeners needed on the body. We attach the mouse up and
   * mouse move event on the body, not the resizer element for a couple
   * reasons:
   *
   * 1. We want the relative position on the body, not the resizer's position
   * when determining how much the user has moved.
   *
   * 2. The user may drag fast or in a way that makes the mouse cursor move
   * off resizer. This means if you mouse up on another element it won't
   * trigger the mouse up event causing an issue of never ending resizing
   * until you moused up on the resizer specifically.
   *
   * @private
   */
  BatchGeoVerticalResizer.prototype._attachBodyListeners = function () {
    var self = this;
    var state = self._dragState;
    var eventNames = this._getEventNames();

    $("body")
      .on(eventNames.up, function (event) {
        // Since this is on the body we should make sure they mousedown
        // on the resizer element or else this triggers for ALL mouse up
        // events on the body
        if (state.mouseDown) {
          state.mouseDown = false;
          var height = self._findEvent(event)
            ? state.startingYPosition - self._findEvent(event).screenY
            : null;
          self.settings.onEnd(self._createEventObject(event, height));
        }
      })
      .on(eventNames.move, function (event) {
        if (state.mouseDown) {
          self.settings.onResize(
            self._createEventObject(
              event,
              state.startingYPosition - self._findEvent(event).screenY,
            ),
          );
        }
      });
  };

  window.BatchGeoVerticalResizer = BatchGeoVerticalResizer;
})(window);
